<script lang="ts">
export default {
  inheritAttrs: false
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { CheckIcon } from '@heroicons/vue/20/solid'

import SlideFadeTransition from '@/components/SlideFadeTransition.vue'
import Input from "@/components/Input.vue";

interface Option {
  key: string,
  label: string,
}

const props = defineProps<{
  label: string,
  options: Option[],
  multiple?: boolean,
  maxOptionsInLabel?: number,
  expand?: boolean,
  marginless?: boolean,
  floating?: boolean,
  modelValue: Option | Option[],
  showLabel?: boolean,
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: Option | Option[]): void,
}>()

const isExpanded = ref(props.expand || false);

const options = computed(() => props.options);

const selectedOptions = computed(() => {
  return Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
});

function isOptionSelected(option: Option): boolean {
  return selectedOptions.value.map(option => option.key).includes(option.key);
}

function toggleOption(option: Option) {
  let selection = selectedOptions.value;
  if (props.multiple) {
    if (isOptionSelected(option)) {
      selection = selection.filter(opt => opt.key !== option.key);
    } else {
      selection.push(option);
    }
  } else {
    selection = [option];
    isExpanded.value = false;
    query.value = '';
  }

  emit('update:modelValue', props.multiple ? selection : selection[0]);
}

function selectOption(option: Option) {
  isExpanded.value = false;
  query.value = '';
  emit('update:modelValue', props.multiple ? [option] : option);
}

const query = ref('')
const filteredOptions = computed(() =>
    query.value === '' ? options.value : options.value.filter(option =>
        option.label
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(query.value.toLowerCase().replace(/\s+/g, ''))
    )
)

const selectedOptionLabels: string[] = computed(() => {
  let labels = selectedOptions.value.map(option => option.label).sort((optionA, optionB) => optionA.localeCompare(optionB));

  if (props.maxOptionsInLabel !== undefined && labels.length > props.maxOptionsInLabel) {
    labels = [`${labels.length} selected`];
  }

  return labels;
})

const isAllSelected = computed(() => selectedOptions.value.length === options.value.length);

function toggleAll() {
  if (isAllSelected.value) {
    emit('update:modelValue', []);
  } else {
    emit('update:modelValue', options.value);
  }
}

function onMouseLeave() {
  if (isExpanded.value) {
    isExpanded.value = false;
  }
}
</script>

<template>
  <div :class="[marginless ? '' : 'mb-2']" @mouseleave="onMouseLeave">
    <label v-if="showLabel" class="label">{{ label }}</label>
    <div :class="['boxed preview', isExpanded ? 'is-expanded' : '']" @click="isExpanded = !isExpanded" v-bind="$attrs">
      <template v-if="showLabel">
        <span v-if="selectedOptions.length > 0">
          <span class="font-medium">{{ selectedOptionLabels.join(', ') }}</span>
        </span>
        <span v-else class="italic font-medium">Nothing selected</span>
      </template>
      <template v-else>
        {{ label }}<span v-if="selectedOptions.length > 0">:
          <span class="font-medium">{{ selectedOptionLabels.join(', ') }}</span>
        </span>
      </template>
    </div>
    <SlideFadeTransition>
      <div :class="['options', floating ? 'is-floating' : '']" v-if="isExpanded">
        <div class="boxed search">
          <div class="flex flex-grow items-center">
            <label for="query" class="sr-only">Search</label>
            <input label="query" id="query" name="query" v-model="query" autocomplete="off" v-focus placeholder="Search ..."/>
          </div>
          <template v-if="multiple">
            <div class="all-toggle pl-5">
              <a href="#" class="default-anchor" @click.prevent="toggleAll()">{{ isAllSelected ? 'none' : 'all' }}</a>
            </div>
          </template>
        </div>
        <div v-for="option in filteredOptions" class="boxed">
          <div class="flex items-center justify-between">
            <div class="flex flex-grow items-center cursor-pointer" @click="toggleOption(option)">
              <div v-if="isOptionSelected(option)" class="mr-2">
                <CheckIcon class="h-5 w-5" aria-hidden="true"/>
              </div>
              <div>{{ option.label }}</div>
            </div>
            <template v-if="multiple">
              <div class="only-this">
                <a href="#" class="default-anchor" @click.prevent="selectOption(option)">only</a>
              </div>
            </template>
          </div>
        </div>
      </div>
    </SlideFadeTransition>
  </div>
</template>

<style scoped lang="scss">
label {
  @apply block mb-0.5 font-bold text-xs;
  @apply text-gray-300 dark:text-gray-500;
}

.boxed {
  @apply relative block w-full appearance-none px-3 py-2;
  @apply bg-white dark:bg-gray-700;
  @apply border border-gray-300 dark:border-gray-500;
  @apply text-gray-900 dark:text-gray-200;

  &.preview {
    @apply font-bold cursor-pointer rounded-lg;

    &.is-expanded {
      @apply rounded-b-none;
    }
  }

  &.search {
    @apply flex items-center justify-between;

    input {
      @apply bg-transparent border-0 border-b border-gray-700 dark:border-gray-400 outline-none w-full;
      @apply placeholder-gray-800 dark:placeholder-gray-600;
    }
  }
}

.options {
  @apply absolute block z-10 pr-1 pb-1 -mr-1 -mb-1 w-96 max-h-64 overflow-y-auto;

  .boxed {
    @apply border-t-0;

    &:last-child {
      @apply rounded-b-md;
    }
  }

  &.is-floating .boxed:first-child {
    @apply mt-1 border-t rounded-t-md;
  }

  .only-this, .all-toggle {
    @apply text-right text-xs;
  }
}
</style>
